import React, { useState, useEffect } from 'react';
import { getUserLimits } from '../../utils/api';
import { HardDrive, Calendar, MessageSquare, AlertTriangle, Crown } from 'lucide-react';

/**
 * QuotaWidget - Displays user storage and quota usage
 * Shows warnings when approaching limits
 */
const QuotaWidget = ({ onUpgradeClick }) => {
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuotaData();
  }, []);

  const loadQuotaData = async () => {
    try {
      const response = await getUserLimits();
      setQuotaData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading quota data:', err);
      setError('Impossible de charger les quotas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse shadow-sm">
        <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
        <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  if (error || !quotaData) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
        <p className="text-red-600 text-sm">{error || 'Erreur de chargement'}</p>
      </div>
    );
  }

  const { limits, usage, is_pro } = quotaData;
  
  // Calculate percentages
  const storagePercentage = usage.storage_percentage || 0;
  const eventsPercentage = limits.max_active_events > 0 
    ? (usage.active_events / limits.max_active_events) * 100 
    : 0;

  // Determine warning levels
  const storageWarning = storagePercentage >= 80;
  const storageCritical = storagePercentage >= 95;
  const eventsWarning = eventsPercentage >= 80;
  const eventsCritical = usage.active_events >= limits.max_active_events;

  // Progress bar color based on usage
  const getProgressColor = (percentage, warning, critical) => {
    if (critical) return 'bg-red-500';
    if (warning) return 'bg-amber-500';
    if (percentage >= 50) return 'bg-violet-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          {is_pro ? (
            <>
              <Crown className="w-4 h-4 text-amber-500" />
              Plan Pro
            </>
          ) : (
            <>
              <HardDrive className="w-4 h-4 text-violet-600" />
              Quotas Gratuit
            </>
          )}
        </h3>
        {!is_pro && (
          <button
            onClick={onUpgradeClick}
            className="text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors"
          >
            Passer Pro →
          </button>
        )}
      </div>

      {/* Storage Usage */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 flex items-center gap-1.5">
            <HardDrive className="w-3.5 h-3.5" />
            Stockage
          </span>
          <span className={`font-medium ${storageCritical ? 'text-red-600' : storageWarning ? 'text-amber-600' : 'text-gray-700'}`}>
            {usage.storage_used_formatted} / {limits.max_storage_formatted}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${getProgressColor(storagePercentage, storageWarning, storageCritical)}`}
            style={{ width: `${Math.min(storagePercentage, 100)}%` }}
          />
        </div>
        {storageWarning && (
          <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
            <AlertTriangle className="w-3 h-3" />
            {storageCritical ? 'Stockage presque plein !' : 'Stockage à plus de 80%'}
          </p>
        )}
      </div>

      {/* Events Usage */}
      {!is_pro && limits.max_active_events > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Événements
            </span>
            <span className={`font-medium ${eventsCritical ? 'text-red-600' : eventsWarning ? 'text-amber-600' : 'text-gray-700'}`}>
              {usage.active_events} / {limits.max_active_events}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor(eventsPercentage, eventsWarning, eventsCritical)}`}
              style={{ width: `${Math.min(eventsPercentage, 100)}%` }}
            />
          </div>
          {eventsCritical && (
            <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3" />
              Limite atteinte ! Archivez un événement.
            </p>
          )}
        </div>
      )}

      {/* Suggestions Info (only for free tier) */}
      {!is_pro && (
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              Suggestions par événement
            </span>
            <span className="text-gray-600 font-medium">
              max {limits.max_suggestions_per_event}
            </span>
          </div>
        </div>
      )}

      {/* Upgrade CTA for free users near limit */}
      {!is_pro && (storageWarning || eventsCritical) && (
        <button
          onClick={onUpgradeClick}
          className="w-full mt-2 py-2 px-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-semibold rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 shadow-md"
        >
          <Crown className="w-4 h-4" />
          Passer au Plan Pro - Plus de limites !
        </button>
      )}
    </div>
  );
};

export default QuotaWidget;
